import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  VideoLibrary as VideoLibraryIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Whatshot as WhatshotIcon,
  Upload as UploadIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import StatisticsCard from '../components/common/StatisticsCard';
import ChartCard from '../components/common/ChartCard';
import LatestAdCard from '../components/common/LatestAdCard';
import { statisticsService } from '../services/statistics';

// AdsFather-themed taglines for the homepage
const HOMEPAGE_TAGLINES = [
  // Campaign Performance
  "Success is measured in conversions, not just impressions.",
  "Every click tells a story. Make yours legendary.",
  "From upload to conversion: The Ads Father way.",
  "Data-driven decisions, godfather-approved results.",

  // Creative Excellence
  "Your video deserves more than just views... it deserves results.",
  "In the family of ads, quality is not an option.",
  "Creative excellence: Because mediocrity is not an offer we make.",
  "From script to screen to success: The complete ad lifecycle.",

  // Platform Mastery
  "YouTube, Meta, Google... all platforms bow to data.",
  "Multi-platform mastery: One upload, infinite possibilities.",
  "Cross-platform campaigns that hit harder than a Sicilian message.",
  "Unified uploads, fragmented audiences: Our specialty.",

  // Analytics & Insights
  "Numbers don't lie. Neither do we about campaign performance.",
  "ROI is the new ROI: Return on Intelligence.",
  "Analytics that reveal what audiences really want.",
  "Data is our consigliere: Always giving the best advice.",

  // Production Management
  "From production to publication: Seamless ad workflows.",
  "Manage campaigns like a family business.",
  "Production planning with the precision of a Sicilian vendetta.",
  "Creative control: Because every ad has a godfather.",

  // Music & Licensing
  "Licensed tracks that set the perfect mood for conversions.",
  "Music licensing: Because silence isn't golden in ads.",
  "From royalty-free to royalty-earning: Our licensed soundtracks.",
  "Audio that amplifies your message, not your costs.",

  // Job Management
  "Upload once, deploy everywhere: Automated ad distribution.",
  "Job queues that never sleep, just like our dedication.",
  "From pending to published: Tracking every step.",
  "Status updates you can count on, like family loyalty.",

  // Growth & Scale
  "Scale campaigns like empires: Big, bold, and profitable.",
  "Growth hacking with a Sicilian twist.",
  "From startup to syndicate: Scaling campaigns that work.",
  "Massive reach, personal touch: The Ads Father paradox.",
];

const HomePage: React.FC = () => {
  const [currentTagline, setCurrentTagline] = useState('');

  // Fetch statistics data
  const {
    data: stats,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['homepage-statistics'],
    queryFn: () => statisticsService.getStatistics(),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
  });

  useEffect(() => {
    // Set a random tagline on component mount
    setCurrentTagline(HOMEPAGE_TAGLINES[Math.floor(Math.random() * HOMEPAGE_TAGLINES.length)]);
  }, []);

  const handleRefresh = () => {
    statisticsService.clearCache();
    refetch();
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          Failed to load statistics: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
          >
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        {/* Header Section with Logo and Tagline */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #ff6b35, #f7931e)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            TheAdsFather
          </Typography>

          {/* Animated tagline */}
          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              fontStyle: 'italic',
              minHeight: '3rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 2,
              animation: 'fadeIn 0.8s ease-in',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            "{currentTagline}"
          </Typography>

          {/* Refresh button */}
          <Box sx={{ mt: 2 }}>
            <IconButton
              onClick={handleRefresh}
              disabled={isRefetching}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
              }}
            >
              {isRefetching ? (
                <CircularProgress size={24} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Statistics Cards Grid */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatisticsCard
              title="Total Ads"
              value={stats?.totalAds || 0}
              subtitle="Creative assets in library"
              icon={VideoLibraryIcon}
              loading={isLoading}
              color="primary"
              trend={stats?.recentAds ? {
                value: Math.round((stats.recentAds / Math.max(stats.totalAds, 1)) * 100),
                label: 'added this month'
              } : undefined}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatisticsCard
              title="Unique Creatives"
              value={stats?.totalCreatives || 0}
              subtitle="Distinct creative numbers"
              icon={CategoryIcon}
              loading={isLoading}
              color="secondary"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatisticsCard
              title="Productions"
              value={stats?.totalProductions || 0}
              subtitle="Campaign collections"
              icon={TrendingUpIcon}
              loading={isLoading}
              color="success"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatisticsCard
              title="Ads Uploaded"
              value={stats?.totalUploaded || 0}
              subtitle="To YouTube or Meta"
              icon={UploadIcon}
              loading={isLoading}
              color="info"
              trend={stats?.totalAds ? {
                value: Math.round((stats.totalUploaded / Math.max(stats.totalAds, 1)) * 100),
                label: 'of total ads'
              } : undefined}
            />
          </Grid>
        </Grid>

        {/* Latest Ad Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Latest Upload
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <LatestAdCard ad={stats?.latestAd} loading={isLoading} />
            </Grid>
            <Grid item xs={12}>
              <ChartCard
                title="Platform Distribution"
                data={stats ? statisticsService.getPlatformMarkersChartData(stats) : null}
                type="doughnut"
                loading={isLoading}
                height={400}
                color="info"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Metadata Breakdowns Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Metadata Analytics
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <ChartCard
                title="Cast Distribution"
                data={stats ? statisticsService.getCastChartData(stats) : null}
                type="doughnut"
                loading={isLoading}
                height={350}
                color="primary"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard
                title="Hero Age Distribution"
                data={stats ? statisticsService.getHeroAgeChartData(stats) : null}
                type="doughnut"
                loading={isLoading}
                height={350}
                color="secondary"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard
                title="Hero Gender Distribution"
                data={stats ? statisticsService.getHeroGenderChartData(stats) : null}
                type="doughnut"
                loading={isLoading}
                height={350}
                color="success"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <ChartCard
                title="Piano Type Distribution"
                data={stats ? statisticsService.getPianoTypeChartData(stats) : null}
                type="doughnut"
                loading={isLoading}
                height={350}
                color="warning"
              />
            </Grid>

            <Grid item xs={12}>
              <ChartCard
                title="Top 10 Meant To Tags"
                data={stats ? statisticsService.getTopUSPsChartData(stats, 10) : null}
                type="bar"
                loading={isLoading}
                height={350}
                color="info"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Decorative corner elements */}
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            left: 20,
            opacity: 0.05,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          <WhatshotIcon sx={{ fontSize: 48 }} />
        </Box>
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            opacity: 0.05,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        >
          <WhatshotIcon sx={{ fontSize: 48 }} />
        </Box>
      </Container>
    </Box>
  );
};

export default HomePage;

